/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

// eslint-disable-next-line no-restricted-imports
import type { AnchorBlock } from '@/features/contentstack/interface/enhancedPdpTypes';
// eslint-disable-next-line no-restricted-imports
import type { EnhancedPDPGraphQLResponse } from '@/features/contentstack/lib/common/enhancedPdpContentTypes';

export const ANCHOR_POSITIONS = {
  TOP: 'Anchor Top',
  MIDDLE: 'Anchor Middle',
  BOTTOM: 'Anchor Bottom',
} as const;

export type AnchorPosition = (typeof ANCHOR_POSITIONS)[keyof typeof ANCHOR_POSITIONS];

export const SPECIFICITY_LEVELS = {
  BRAND_AND_SKU: 4,
  BRAND_AND_TAXONOMY: 3,
  BRAND_ONLY: 2,
  TAXONOMY_ONLY: 1,
} as const;

export type SpecificityLevel = (typeof SPECIFICITY_LEVELS)[keyof typeof SPECIFICITY_LEVELS];

export type MatchedAnchors = {
  top: { anchor: AnchorBlock; specificity: string; uid?: string } | null;
  middle: { anchor: AnchorBlock; specificity: string; uid?: string } | null;
  bottom: { anchor: AnchorBlock; specificity: string; uid?: string } | null;
};

// Ordered from highest to lowest specificity — first match wins
const SPECIFICITY_ORDER = [
  { key: 'brandAndSku', specificity: SPECIFICITY_LEVELS.BRAND_AND_SKU },
  { key: 'brandAndTaxonomy', specificity: SPECIFICITY_LEVELS.BRAND_AND_TAXONOMY },
  { key: 'brandOnly', specificity: SPECIFICITY_LEVELS.BRAND_ONLY },
  { key: 'taxonomyOnly', specificity: SPECIFICITY_LEVELS.TAXONOMY_ONLY },
] as const;

/**
 * For a given anchor position, find the best matching AnchorBlock from the
 * GraphQL response. Walks specificity buckets from highest (4) to lowest (1)
 * and returns the first bucket that has content for the requested position.
 *
 * Returns null if no Enhanced entry has content for this anchor → caller falls
 * back to Default PDP content for that anchor.
 */
function matchAnchorForPosition(
  response: EnhancedPDPGraphQLResponse,
  position: AnchorPosition,
): { anchor: AnchorBlock; specificity: string; uid?: string } | null {
  for (const { key } of SPECIFICITY_ORDER) {
    const anchorBlocks = response[key]?.items?.[0]?.anchor_block;
    const uid = response[key]?.items?.[0]?.system.uid;
    const specificity = key;

    if (!Array.isArray(anchorBlocks) || anchorBlocks.length === 0) {
      continue;
    }

    const anchor = anchorBlocks.find((b) => b.anchor_position === position);
    if (anchor) {
      return { anchor, specificity, uid };
    }
  }

  return null;
}

/**
 * Match Enhanced PDP entries per anchor independently.
 *
 * Each anchor (top/middle/bottom) is evaluated separately — the same Enhanced
 * entry can win for `top` but lose for `middle` if a more specific entry has
 * `middle` content.
 *
 * Specificity ranking (highest wins):
 *   4 = Brand + SKU
 *   3 = Brand + Taxonomy
 *   2 = Brand only
 *   1 = Taxonomy only
 */
export function getMostSpecificEntry(response: EnhancedPDPGraphQLResponse): MatchedAnchors {
  return {
    top: matchAnchorForPosition(response, ANCHOR_POSITIONS.TOP),
    middle: matchAnchorForPosition(response, ANCHOR_POSITIONS.MIDDLE),
    bottom: matchAnchorForPosition(response, ANCHOR_POSITIONS.BOTTOM),
  };
}
